// @ts-nocheck
// Shared likes/skips document for the planner funnel and sitewide swipe overlay.
// Writes only likes, skips, undo, swipeHintSeen — never body data or screen.
// Also the one place for the planner document's storage key, version and shared labels: the funnel, Explore and
// Profil take them from here (readDoc() is the read-only view of the whole document, goals included).
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpPlannerLikes) return;

  const STORAGE_KEY = 'vhp_planner_anon';
  const STATE_VERSION = 6;
  const LIKES_VERSION = 1;
  const LIKE_READY = 3;
  const LIKE_VARIETY = 10;
  const SLOT_ORDER = ['breakfast', 'main', 'snack', 'dessert'];
  const SLOT_LABEL = { breakfast: tr('common.slot_breakfast'), main: tr('common.slot_main'), snack: tr('common.slot_snack'), dessert: tr('common.slot_dessert') };
  const GOAL_LABEL = { lose: tr('common.goal_lose'), maintain: tr('common.goal_maintain'), gain: tr('common.goal_gain') };
  const DEFAULT_SPLIT = { protein: 30, carbs: 45, fat: 25 };

  const productId = (id) => {
    const match = String(id ?? '').match(/(\d+)$/);
    return match ? match[1] : '';
  };

  const blankLikes = () => ({ v: LIKES_VERSION, items: [] });
  const blankSkips = () => ({ v: LIKES_VERSION, items: [] });

  const numOrNull = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const normalizeLikeItem = (item) => {
    if (!item || typeof item !== 'object') return null;
    const id = productId(item.id);
    if (!id || !SLOT_ORDER.includes(item.slot)) return null;
    return {
      id,
      handle: typeof item.handle === 'string' ? item.handle : '',
      title: typeof item.title === 'string' ? item.title : '',
      image: typeof item.image === 'string' ? item.image : '',
      slot: item.slot,
      kcal: item.kcal ?? null,
      protein: item.protein ?? null,
      carbs: item.carbs ?? null,
      fat: item.fat ?? null,
      time: item.time ?? null,
      at: typeof item.at === 'string' ? item.at : null,
    };
  };

  const normalizeLikes = (raw) => {
    const items = [];
    const seen = new Set();
    (Array.isArray(raw?.items) ? raw.items : []).forEach((item) => {
      const next = normalizeLikeItem(item);
      if (!next || seen.has(next.id)) return;
      seen.add(next.id);
      items.push(next);
    });
    return { v: LIKES_VERSION, items };
  };

  const normalizeSkips = (raw) => {
    const items = [];
    const seen = new Set();
    (Array.isArray(raw?.items) ? raw.items : []).forEach((item) => {
      const id = productId(item?.id);
      if (!id || !SLOT_ORDER.includes(item?.slot) || seen.has(id)) return;
      seen.add(id);
      items.push({ id, slot: item.slot, at: typeof item.at === 'string' ? item.at : null });
    });
    return { v: LIKES_VERSION, items };
  };

  // Tombstones: a like / skip the person took back, so a sync can't resurrect it from another device.
  const REMOVED_MAX = 300;
  const normalizeRemoved = (raw) => {
    const items = [];
    const seen = new Set();
    (Array.isArray(raw) ? raw : []).forEach((row) => {
      const id = productId(row?.id);
      const at = typeof row?.at === 'string' && Number.isFinite(Date.parse(row.at)) ? row.at : null;
      if (!id || !at || seen.has(id)) return;
      seen.add(id);
      items.push({ id, at });
    });
    return items.slice(-REMOVED_MAX);
  };

  const normalizeUndo = (raw) => {
    if (raw?.action === 'like') {
      const item = normalizeLikeItem(raw.item);
      return item ? { action: 'like', item } : null;
    }
    if (raw?.action === 'skip') {
      const id = productId(raw.item?.id);
      return id && SLOT_ORDER.includes(raw.item?.slot)
        ? { action: 'skip', item: { id, slot: raw.item.slot, at: raw.item.at || null } }
        : null;
    }
    return null;
  };

  const readRaw = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      return raw && typeof raw === 'object' ? raw : {};
    } catch (e) {
      return {};
    }
  };

  const snapshot = (raw) => {
    const likes = normalizeLikes(raw.likes);
    const skips = normalizeSkips(raw.skips);
    const likedIds = new Set(likes.items.map((item) => item.id));
    skips.items = skips.items.filter((item) => !likedIds.has(item.id));
    return {
      likes,
      skips,
      removed: normalizeRemoved(raw.removed),
      undo: normalizeUndo(raw.undo),
      swipeHintSeen: Boolean(raw.swipeHintSeen),
    };
  };

  const read = () => snapshot(readRaw());

  // options.silent: the change comes from a sync (merged server data), so no tombstones and no "push again".
  const patch = (mutator, options) => {
    const silent = Boolean(options?.silent);
    const raw = readRaw();
    if (raw.v == null || Number(raw.v) < 1) raw.v = STATE_VERSION;
    const next = snapshot(raw);
    raw.likes = next.likes;
    raw.skips = next.skips;
    raw.removed = next.removed;
    raw.undo = next.undo;
    raw.swipeHintSeen = next.swipeHintSeen;
    const beforeLikes = new Set(raw.likes.items.map((item) => item.id));
    const beforeSkips = new Set(raw.skips.items.map((item) => item.id));
    if (typeof mutator === 'function') mutator(raw);
    raw.likes = normalizeLikes(raw.likes);
    raw.skips = normalizeSkips(raw.skips);
    const likedIds = new Set(raw.likes.items.map((item) => item.id));
    raw.skips.items = raw.skips.items.filter((item) => !likedIds.has(item.id));
    const skippedIds = new Set(raw.skips.items.map((item) => item.id));
    raw.undo = normalizeUndo(raw.undo);
    raw.swipeHintSeen = Boolean(raw.swipeHintSeen);

    let removed = normalizeRemoved(raw.removed);
    if (!silent) {
      const now = new Date().toISOString();
      const gone = new Set();
      beforeLikes.forEach((id) => {
        if (!likedIds.has(id) && !skippedIds.has(id)) gone.add(id);
      });
      beforeSkips.forEach((id) => {
        if (!likedIds.has(id) && !skippedIds.has(id)) gone.add(id);
      });
      removed = removed.filter((row) => !gone.has(row.id)).concat([...gone].map((id) => ({ id, at: now })));
    }
    // A tombstone only matters while nothing newer exists for that recipe.
    raw.removed = removed.filter((row) => !likedIds.has(row.id) && !skippedIds.has(row.id)).slice(-REMOVED_MAX);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
    } catch (e) {
      /* private mode: in-memory only for this page view */
    }
    try {
      window.dispatchEvent(new CustomEvent('vhp:likes-changed', { detail: { silent } }));
    } catch (e) {
      /* ignore */
    }
    return snapshot(raw);
  };

  /* ------------------------------------------------------------ account sync helpers */

  const ms = (at) => {
    const t = at ? Date.parse(at) : 0;
    return Number.isFinite(t) ? t : 0;
  };

  const addLike = (item) => {
    const next = normalizeLikeItem({ ...item, at: item.at || new Date().toISOString() });
    if (!next) return false;
    patch((raw) => {
      raw.likes.items = raw.likes.items.filter((row) => row.id !== next.id).concat([next]);
      raw.skips.items = raw.skips.items.filter((row) => row.id !== next.id);
    });
    return true;
  };

  const removeLike = (id) => {
    const key = productId(id);
    patch((raw) => {
      raw.likes.items = raw.likes.items.filter((row) => row.id !== key);
    });
  };

  // Likes newest first (default), by protein (highest first) or by time (quickest first, unknown last).
  const sortedLikes = (order) => {
    const items = read().likes.items.slice();
    if (order === 'protein') {
      return items.sort((a, b) => (Number(b.protein) || 0) - (Number(a.protein) || 0) || ms(b.at) - ms(a.at));
    }
    if (order === 'quick') {
      const mins = (item) => Number(item.time) || Infinity;
      return items.sort((a, b) => mins(a) - mins(b) || ms(b.at) - ms(a.at));
    }
    return items.sort((a, b) => ms(b.at) - ms(a.at));
  };

  const cleanNum = (value) => {
    const n = numOrNull(value);
    return n == null ? null : n;
  };

  // The document POST /api/account takes (and returns). Same shape as the server's LikesDoc.
  const exportDoc = () => {
    const doc = read();
    return {
      v: 1,
      likes: doc.likes.items.map((item) => ({
        id: item.id,
        handle: String(item.handle || '').slice(0, 200),
        title: String(item.title || '').slice(0, 300),
        image: String(item.image || '').slice(0, 600),
        slot: item.slot,
        kcal: cleanNum(item.kcal),
        protein: cleanNum(item.protein),
        carbs: cleanNum(item.carbs),
        fat: cleanNum(item.fat),
        time: cleanNum(item.time),
        at: item.at || null,
      })),
      skips: doc.skips.items.map((item) => ({ id: item.id, slot: item.slot, at: item.at || null })),
      removed: doc.removed.map((row) => ({ id: row.id, at: row.at })),
    };
  };

  // Newest event per recipe wins (removed > like > skip on a tie). Mirrors src/lib/appAccount.ts mergeLikes.
  const mergeDocs = (a, b) => {
    const RANK = { removed: 3, like: 2, skip: 1 };
    const ceiling = Date.now() + 24 * 3600 * 1000;
    const best = new Map();
    const offer = (kind, item) => {
      const at = Math.min(ms(item.at), ceiling);
      const cur = best.get(item.id);
      if (!cur || at > cur.at || (at === cur.at && RANK[kind] > RANK[cur.kind])) best.set(item.id, { kind, at, item });
    };
    [a, b].forEach((doc) => {
      (doc?.likes || []).forEach((item) => offer('like', item));
      (doc?.skips || []).forEach((item) => offer('skip', item));
      (doc?.removed || []).forEach((item) => offer('removed', item));
    });
    const events = [...best.values()].sort((x, y) => x.at - y.at);
    const keep = Date.now() - 120 * 24 * 3600 * 1000;
    return {
      v: 1,
      likes: events.filter((e) => e.kind === 'like').map((e) => e.item),
      skips: events.filter((e) => e.kind === 'skip').map((e) => e.item),
      removed: events.filter((e) => e.kind === 'removed' && e.at >= keep).map((e) => ({ id: e.item.id, at: new Date(e.at).toISOString() })),
    };
  };

  // Replace local likes / skips / tombstones with a merged document (no tombstone side effects).
  const applyDoc = (doc) => {
    patch(
      (raw) => {
        raw.likes = { v: LIKES_VERSION, items: doc.likes || [] };
        raw.skips = { v: LIKES_VERSION, items: doc.skips || [] };
        raw.removed = doc.removed || [];
      },
      { silent: true }
    );
  };

  const likeItems = (doc) => (doc || read()).likes.items;
  const likeCount = (doc) => likeItems(doc).length;
  const hasLikedMain = (doc) => likeItems(doc).some((item) => item.slot === 'main');
  const planReady = (doc) => likeCount(doc) >= LIKE_READY && hasLikedMain(doc);

  const likeLabel = (doc) => {
    const n = likeCount(doc);
    const main = hasLikedMain(doc);
    if (n >= LIKE_READY && !main) return tr('week.label_need_main');
    if (n >= LIKE_VARIETY) return tr('week.label_optimal');
    if (n >= LIKE_READY) {
      const left = LIKE_VARIETY - n;
      return tr('week.label_ready', { n: left });
    }
    if (n === 0) return tr('week.label_start');
    const left = LIKE_READY - n;
    return tr('week.label_left', { n: left });
  };

  const likeHint = (doc) => {
    const items = likeItems(doc);
    const hasBreakfast = items.some((item) => item.slot === 'breakfast');
    const hasSnack = items.some((item) => item.slot === 'snack');
    if (!hasBreakfast || !hasSnack) return tr('common.auto_sides');
    return '';
  };

  const planButtonLabel = (doc, paid) => {
    if (paid) return tr('common.to_week');
    if (!hasLikedMain(doc)) return tr('week.btn_need_main');
    const n = likeCount(doc);
    if (n < LIKE_READY) {
      const left = LIKE_READY - n;
      return tr('week.btn_left', { n: left });
    }
    return tr('common.create_plan');
  };

  const WEEK_MAINS = 5;
  const WEEK_DAYS = tr.list('common.days_short').slice(0, 5);

  // Mo–Fr fills from main likes in like order. Breakfast and snacks stay off the strip.
  const weekView = (doc) => {
    const items = likeItems(doc);
    const mains = items.filter((item) => item.slot === 'main');
    const slots = WEEK_DAYS.map((day, index) => {
      const item = mains[index] || null;
      let state = 'empty';
      if (item) state = 'filled';
      else if (index === mains.length) state = 'next';
      return { day, item, state };
    });
    return {
      days: WEEK_DAYS,
      goal: WEEK_MAINS,
      mains,
      slots,
      filled: Math.min(mains.length, WEEK_MAINS),
      remaining: Math.max(0, WEEK_MAINS - mains.length),
      extra: mains.slice(WEEK_MAINS),
      sides: items.filter((item) => item.slot !== 'main'),
      ready: planReady(doc),
      // What the ready rule still needs (the dock button says it).
      readyLeft: Math.max(0, LIKE_READY - likeCount(doc)),
      needMain: !hasLikedMain(doc),
    };
  };

  window.vhpPlannerLikes = {
    STORAGE_KEY,
    STATE_VERSION,
    LIKES_VERSION,
    LIKE_READY,
    LIKE_VARIETY,
    SLOT_ORDER,
    SLOT_LABEL,
    GOAL_LABEL,
    DEFAULT_SPLIT,
    readDoc: readRaw,
    productId,
    numOrNull,
    blankLikes,
    blankSkips,
    normalizeLikeItem,
    normalizeLikes,
    normalizeSkips,
    read,
    patch,
    addLike,
    removeLike,
    sortedLikes,
    exportDoc,
    mergeDocs,
    applyDoc,
    likeItems,
    likeCount,
    hasLikedMain,
    planReady,
    likeLabel,
    likeHint,
    planButtonLabel,
    WEEK_MAINS,
    WEEK_DAYS,
    weekView,
  };

  // "Has she ever added a dish?": html.vhp-saved-once, kept in localStorage so removing every like later does not bring
  // back the first-time cues (the feed's "In den Wochenplan" light sweep, assets/vhp-feed-reel.css).
  const SAVED_ONCE_KEY = 'vhp_saved_once';
  const markSavedOnce = () => {
    let known = false;
    try {
      known = localStorage.getItem(SAVED_ONCE_KEY) === '1';
    } catch (e) {}
    if (!known && likeCount() === 0) return;
    document.documentElement.classList.add('vhp-saved-once');
    if (known) return;
    try {
      localStorage.setItem(SAVED_ONCE_KEY, '1');
    } catch (e) {}
  };
  markSavedOnce();
  window.addEventListener('vhp:likes-changed', markSavedOnce);
})();
